// Bloco de conteúdos da disciplina. MVP: cadastra "file" como link externo
// (Google Drive, Dropbox, URL direta). Pasta/upload nativo virá depois.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  RefreshCw,
  Trash2
} from "lucide-react";
import { Checkbox } from "@app/ui/components/checkbox";
import { api, ApiError, type TeacherContentRow } from "../api";
import { openAlert } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface OfferingLite {
  id: string;
  turmaName: string;
  turmaCode: string | null;
}

const phase = signal<"idle" | "loading" | "ready" | "error">("idle");
const items = signal<TeacherContentRow[]>([]);
const errMsg = signal<string | null>(null);

async function load(offeringId: string) {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listContents({ offeringId });
    items.value = r.contents;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function ConteudosSection({ offerings }: { offerings: OfferingLite[] }) {
  const [activeOfferingId, setActiveOfferingId] = useState<string>(
    offerings[0]?.id ?? ""
  );
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (activeOfferingId) void load(activeOfferingId);
  }, [activeOfferingId]);

  async function add() {
    if (!name.trim() || !url.trim()) {
      void openAlert({ title: "Aviso", message: "Preencha nome e URL." });
      return;
    }
    setSaving(true);
    try {
      const r = await api.createContent({
        offeringId: activeOfferingId,
        kind: "file",
        name: name.trim(),
        url: url.trim(),
        notify
      });
      items.value = [r.content, ...items.value];
      setName("");
      setUrl("");
      setAdding(false);
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string, label: string) {
    if (!confirm(`Excluir "${label}"?`)) return;
    try {
      await api.deleteContent(id);
      items.value = items.value.filter((c) => c.id !== id);
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  if (offerings.length === 0) {
    return (
      <section>
        <h3 class="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <FileText width="1em" height="1em" /> Conteúdos
        </h3>
        <p class="text-xs italic text-muted-foreground">
          Sem turmas vinculadas — peça pro admin pra criar antes de publicar
          conteúdos.
        </p>
      </section>
    );
  }

  return (
    <section>
      <div class="mb-1.5 flex items-center justify-between">
        <h3 class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <FileText width="1em" height="1em" /> Conteúdos
        </h3>
        <div class="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => activeOfferingId && void load(activeOfferingId)}
            title="Atualizar"
            aria-label="Atualizar"
            class="grid size-7 place-items-center rounded-md border border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <RefreshCw className="size-3" />
          </button>
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:bg-accent"
          >
            <Plus className="size-3" />
            {adding ? "Cancelar" : "Adicionar"}
          </button>
        </div>
      </div>

      {offerings.length > 1 && (
        <select
          value={activeOfferingId}
          onChange={(e) => setActiveOfferingId((e.target as HTMLSelectElement).value)}
          class="mb-2 w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        >
          {offerings.map((o) => (
            <option key={o.id} value={o.id}>
              {o.turmaName}{o.turmaCode ? ` (${o.turmaCode})` : ""}
            </option>
          ))}
        </select>
      )}

      {adding && (
        <div class="mb-2 space-y-2 rounded-md border border-border bg-background/50 p-3">
          <input
            type="text"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            placeholder="Nome do material (ex: Aula 1 — slides)"
            maxLength={200}
            class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
          />
          <input
            type="url"
            value={url}
            onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
            placeholder="https://… (Drive, Dropbox, link direto)"
            maxLength={2000}
            class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
          />
          <div class="flex items-center justify-between gap-2">
            <label class="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <Checkbox
                checked={notify}
                onCheckedChange={(v) => setNotify(v === true)}
              />
              Notificar alunos
            </label>
            <button
              type="button"
              onClick={add}
              disabled={saving || !name.trim() || !url.trim()}
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? <Loader2 className="size-3 animate-spin" /> : <Plus className="size-3" />}
              Publicar
            </button>
          </div>
        </div>
      )}

      <div class="space-y-1">
        {phase.value === "loading" && (
          <p class="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Loader2 className="size-3 animate-spin" /> Carregando…
          </p>
        )}
        {phase.value === "error" && (
          <p class="flex items-center gap-1.5 text-[11px] text-destructive">
            <AlertCircle className="size-3" /> {errMsg.value}
          </p>
        )}
        {phase.value === "ready" && items.value.length === 0 && (
          <p class="text-[11px] italic text-muted-foreground">
            Nenhum material publicado nesta turma ainda.
          </p>
        )}
        {items.value.map((c) => {
          const fileUrl = typeof c.payload?.url === "string" ? c.payload.url : "";
          return (
            <div
              key={c.id}
              class="flex items-center gap-2 rounded-md border border-border bg-background/40 px-2.5 py-1.5"
            >
              <FileText className="size-3.5 shrink-0 text-muted-foreground" />
              <span class="flex-1 truncate text-[11px] font-semibold">{c.name}</span>
              {fileUrl && (
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5"
                >
                  Abrir <ExternalLink className="size-3" />
                </a>
              )}
              <button
                type="button"
                onClick={() => remove(c.id, c.name)}
                title="Excluir"
                aria-label="Excluir"
                class="grid size-6 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="size-3" />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
